// Schritt 2 des Serienmail-Assistenten — Inhalt: Betreff, Vorlage, der vorhandene
// RichHtmlEditor, Briefpapier/Signatur-Auswahl, Anhaenge und die Platzhalter-Leiste.
// Bewusst NICHT EmailVerfassen.tsx: die Bausteine sind RichHtmlEditor + baueBriefVorschau,
// hier nur fuer den Serienmail-Fall zusammengesetzt (kein An/Cc/Bcc, kein Empfaenger-Chip).
import React, { useRef, useState } from 'react';
import { CircleNotch, Paperclip, Sparkle, X } from '@phosphor-icons/react';
import { ImmoButton } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { FELD, Feld, LABEL, WahlFeld } from '../emails/bereich/Formular';
import { supabase } from '../../../lib/supabase/client';
import RichHtmlEditor from '../emails/RichHtmlEditor';
import { VERFUEGBARE_PLATZHALTER } from '../../../lib/serienmail/platzhalter';
import type { Briefpapier, SetZustand, Signatur, Vorlage, Zustand } from './SerienmailAssistent';
import { speicherName } from '../../../lib/storage/speicherName';

interface SchrittInhaltProps {
  zustand: Zustand;
  setZustand: SetZustand;
  vorlagen: Vorlage[];
  briefpapierListe: Briefpapier[];
  signaturenListe: Signatur[];
}

const SchrittInhalt: React.FC<SchrittInhaltProps> = ({ zustand, setZustand, vorlagen, briefpapierListe, signaturenListe }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadFehler, setUploadFehler] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const vorlageWaehlen = (vorlageId: string) => {
    const vorlage = vorlagen.find((v) => v.id === vorlageId);
    setZustand((z) => ({
      ...z,
      vorlageId: vorlageId || null,
      betreff: vorlage?.betreff_template ?? z.betreff,
      html: vorlage?.html_template ?? z.html,
    }));
  };

  const platzhalterEinfuegen = (schluessel: string) => {
    // Bewusst einfach ans Ende angehaengt statt an der Cursorposition eingefuegt: der
    // RichHtmlEditor kapselt seine contentEditable-Zone und exponiert keine
    // Einfuegeposition nach aussen — funktioniert in beiden Modi (Visuell/HTML).
    setZustand((z) => ({ ...z, html: `${z.html} {{${schluessel}}}` }));
  };

  const dateienHochladen = async (files: FileList | File[]) => {
    setUploading(true);
    setUploadFehler(null);
    for (const file of Array.from(files)) {
      const path = `serienmail/${crypto.randomUUID()}_${speicherName(file.name)}`;
      const { data, error } = await supabase.storage.from('email-anhaenge').upload(path, file);
      if (error) { setUploadFehler(`Upload fehlgeschlagen: ${file.name}`); continue; }
      setZustand((z) => ({ ...z, anhaenge: [...z.anhaenge, { name: file.name, path: data.path, size: file.size }] }));
    }
    setUploading(false);
  };

  const anhangEntfernen = async (path: string) => {
    await supabase.storage.from('email-anhaenge').remove([path]);
    setZustand((z) => ({ ...z, anhaenge: z.anhaenge.filter((a) => a.path !== path) }));
  };

  const { mobil } = useMobilModus();

  return (
    <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 24, padding: mobil ? 14 : 18, gap: 14 }}>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 12 }}>
        {/* Vorlage */}
        {vorlagen.length > 0 && (
          <Feld label="Vorlage (optional)">
            <WahlFeld wert={zustand.vorlageId || ''} onChange={vorlageWaehlen} ariaLabel="Vorlage">
              <option value="">— Ohne Vorlage —</option>
              {vorlagen.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </WahlFeld>
          </Feld>
        )}
        <Feld label="Briefpapier">
          <WahlFeld wert={zustand.briefpapierId} onChange={(id) => setZustand((z) => ({ ...z, briefpapierId: id }))} ariaLabel="Briefpapier">
            <option value="">— Kein Briefpapier —</option>
            {briefpapierListe.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </WahlFeld>
        </Feld>
        <Feld label="Signatur">
          <WahlFeld wert={zustand.signaturId} onChange={(id) => setZustand((z) => ({ ...z, signaturId: id }))} ariaLabel="Signatur">
            <option value="">— Keine Signatur —</option>
            {signaturenListe.map((sg) => <option key={sg.id} value={sg.id}>{sg.name}</option>)}
          </WahlFeld>
        </Feld>
      </div>

      <Feld label="Betreff">
        <input type="text" value={zustand.betreff} onChange={(e) => setZustand((z) => ({ ...z, betreff: e.target.value }))} placeholder="Betreff der Serienmail…" style={FELD} />
      </Feld>

      {/* Platzhalter-Leiste */}
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span className="flex items-center" style={{ ...LABEL, gap: 6 }}><Sparkle size={13} weight="fill" color="var(--acc)" />Platzhalter einfügen</span>
        <div className="flex flex-wrap" style={{ gap: 5 }}>
          {VERFUEGBARE_PLATZHALTER.map((p) => (
            <button key={p.schluessel} type="button" title={p.beschreibung} onClick={() => platzhalterEinfuegen(p.schluessel)} className="cursor-pointer"
              style={{ height: mobil ? 44 : 28, padding: '0 10px', borderRadius: 10, border: 'none', background: 'var(--fill2)', color: 'var(--ink2)', fontSize: 11, fontWeight: 700, fontFamily: 'ui-monospace, Menlo, monospace' }}>
              {`{{${p.schluessel}}}`}
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={LABEL}>Inhalt</span>
        <RichHtmlEditor value={zustand.html} onChange={(html) => setZustand((z) => ({ ...z, html }))} minHeight={260} mobil={mobil} />
      </div>

      {/* Anhaenge */}
      <div className="flex flex-col" style={{ gap: 8 }}>
        <span style={LABEL}>Anhänge (für alle Empfänger gleich)</span>
        {uploadFehler && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>{uploadFehler}</span>}
        {zustand.anhaenge.map((a) => (
          <div key={a.path} className="flex items-center min-w-0" style={{ gap: 10, borderRadius: 14, padding: '6px 6px 6px 12px', background: 'var(--fill2)' }}>
            <Paperclip size={15} color="var(--ink3)" className="shrink-0" />
            <span className="flex-1 min-w-0 truncate" style={{ fontSize: 13, fontWeight: 700 }}>{a.name}</span>
            <span className="immo-zahl" style={{ fontSize: 11, color: 'var(--ink3)' }}>{Math.round(a.size / 1024)} KB</span>
            <button type="button" aria-label={`${a.name} entfernen`} onClick={() => anhangEntfernen(a.path)} className="flex items-center justify-center cursor-pointer shrink-0"
              style={{ width: mobil ? 44 : 32, height: mobil ? 44 : 32, borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
              <X size={13} weight="bold" />
            </button>
          </div>
        ))}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { if (e.target.files && e.target.files.length > 0) { dateienHochladen(e.target.files); e.target.value = ''; } }}
        />
        <div className="flex">
          <ImmoButton groesse={mobil ? 44 : 36} icon={uploading ? CircleNotch : Paperclip} disabled={uploading} onClick={() => fileInputRef.current?.click()}>Anhang hinzufügen</ImmoButton>
        </div>
      </div>
    </section>
  );
};

export default SchrittInhalt;
